import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Send, ThumbsUp, CheckCircle, MessageCircle, HelpCircle, Users } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace("https://", "wss://").replace("http://", "ws://");

const ROLE_STYLES = {
  organizer: { bg: "bg-[#0099CC]/20", text: "text-[#0099CC]", label: "Org" },
  moderator: { bg: "bg-[#F59E0B]/20", text: "text-[#F59E0B]", label: "Mod" },
  speaker: { bg: "bg-[#10B981]/20", text: "text-[#10B981]", label: "Interv" },
  participant: { bg: "bg-[#64748b]/20", text: "text-[#64748b]", label: "" },
};

export default function EventChatWidget({ eventId, token, compact = false }) {
  const [tab, setTab] = useState("chat"); // chat | qa
  const [messages, setMessages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [input, setInput] = useState("");
  const [onlineCount, setOnlineCount] = useState(0);
  const [sending, setSending] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const reconnectRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Load initial data
  useEffect(() => {
    if (!eventId || !token) return;
    axios.get(`${API}/api/event/events/${eventId}/chat/messages?limit=50`, { headers })
      .then(r => setMessages(r.data)).catch(() => {});
    axios.get(`${API}/api/event/events/${eventId}/chat/questions`, { headers })
      .then(r => setQuestions(r.data)).catch(() => {});
  }, [eventId, token]);

  // WebSocket connection
  useEffect(() => {
    if (!eventId || !token) return;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}/api/event/events/${eventId}/chat/ws?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        const hb = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" })); }, 20000);
        ws._hb = hb;
      };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "chat_message") {
            setMessages(prev => [...prev.slice(-200), msg.data]);
          } else if (msg.type === "new_question") {
            setQuestions(prev => [msg.data, ...prev]);
          } else if (msg.type === "question_voted") {
            setQuestions(prev => prev.map(q => q.id === msg.data.id ? { ...q, votes: msg.data.votes } : q));
          } else if (msg.type === "question_answered") {
            setQuestions(prev => prev.map(q => q.id === msg.data.id ? { ...q, answered: true } : q));
          } else if (msg.type === "online_count") {
            setOnlineCount(msg.data.count);
          }
        } catch {}
      };
      ws.onclose = () => {
        clearInterval(ws._hb);
        reconnectRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [eventId, token]);

  // Auto-scroll chat
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const endpoint = tab === "chat"
        ? `${API}/api/event/events/${eventId}/chat/messages`
        : `${API}/api/event/events/${eventId}/chat/questions`;
      await axios.post(endpoint, { text: input.trim() }, { headers });
      setInput("");
    } catch (e) {
      toast.error("Erreur d'envoi");
    }
    setSending(false);
  };

  const voteQuestion = async (qId) => {
    try {
      await axios.post(`${API}/api/event/events/${eventId}/chat/questions/${qId}/vote`, {}, { headers });
    } catch {}
  };

  const markAnswered = async (qId) => {
    try {
      await axios.patch(`${API}/api/event/events/${eventId}/chat/questions/${qId}/answer`, {}, { headers });
    } catch {}
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const roleStyle = (role) => ROLE_STYLES[role] || ROLE_STYLES.participant;

  return (
    <div className="flex flex-col h-full bg-[#0B0F19]" data-testid="event-chat-widget">
      {/* Tab bar */}
      <div className="flex items-center border-b border-[#1E293B] shrink-0">
        <button onClick={() => setTab("chat")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors ${tab === "chat" ? "text-[#0099CC] border-b-2 border-[#0099CC]" : "text-[#64748b] hover:text-white"}`}
          data-testid="chat-tab-messages">
          <MessageCircle className="h-3 w-3" /> Chat {messages.length > 0 && <span className="text-[9px] bg-[#0099CC]/20 text-[#0099CC] rounded-full px-1.5">{messages.length}</span>}
        </button>
        <button onClick={() => setTab("qa")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors ${tab === "qa" ? "text-[#F59E0B] border-b-2 border-[#F59E0B]" : "text-[#64748b] hover:text-white"}`}
          data-testid="chat-tab-qa">
          <HelpCircle className="h-3 w-3" /> Q&A {questions.length > 0 && <span className="text-[9px] bg-[#F59E0B]/20 text-[#F59E0B] rounded-full px-1.5">{questions.length}</span>}
        </button>
        <div className="flex items-center gap-1 px-2 text-[9px] text-[#64748b]">
          <Users className="h-2.5 w-2.5" />{onlineCount}
        </div>
      </div>

      {/* Messages / Questions list */}
      <div className="flex-1 overflow-y-auto" ref={scrollRef}>
        {tab === "chat" ? (
          <div className="p-2 space-y-1">
            {messages.length === 0 && <p className="text-[#4A5568] text-xs text-center py-8">Aucun message</p>}
            {messages.map(m => {
              const rs = roleStyle(m.conference_role);
              return (
                <div key={m.id} className="group px-2 py-1 rounded hover:bg-[#151B2B] transition-colors" data-testid={`chat-msg-${m.id}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-white">{m.user_name}</span>
                    {rs.label && (
                      <span className={`text-[8px] px-1 py-0.5 rounded ${rs.bg} ${rs.text} font-bold`}>{rs.label}</span>
                    )}
                    <span className="text-[9px] text-[#4A5568] ml-auto">
                      {m.created_at ? new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-0.5 break-words">{m.text}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-2 space-y-1.5">
            {questions.length === 0 && <p className="text-[#4A5568] text-xs text-center py-8">Aucune question</p>}
            {questions.map(q => (
              <div key={q.id} className={`p-2 rounded-lg border transition-colors ${q.answered ? "border-[#10B981]/30 bg-[#10B981]/5" : "border-[#2A3441] bg-[#151B2B]"}`} data-testid={`qa-question-${q.id}`}>
                <div className="flex items-start gap-2">
                  <button onClick={() => voteQuestion(q.id)}
                    className="flex flex-col items-center gap-0.5 shrink-0 mt-0.5 group/vote" data-testid={`qa-vote-${q.id}`}>
                    <ThumbsUp className="h-3 w-3 text-[#64748b] group-hover/vote:text-[#F59E0B] transition-colors" />
                    <span className="text-[10px] font-bold text-[#F59E0B]">{q.votes || 0}</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white break-words">{q.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] text-[#4A5568]">{q.user_name}</span>
                      {q.answered && <span className="text-[9px] text-[#10B981] flex items-center gap-0.5"><CheckCircle className="h-2.5 w-2.5" /> Repondu</span>}
                    </div>
                  </div>
                  {!q.answered && (
                    <button onClick={() => markAnswered(q.id)}
                      className="shrink-0 p-1 rounded hover:bg-[#10B981]/20 text-[#64748b] hover:text-[#10B981] transition-colors"
                      title="Marquer comme repondu" data-testid={`qa-mark-answered-${q.id}`}>
                      <CheckCircle className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-2 border-t border-[#1E293B] shrink-0">
        <div className="flex items-center gap-1.5">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
            placeholder={tab === "chat" ? "Message..." : "Poser une question..."}
            className="flex-1 bg-[#151B2B] border border-[#2A3441] rounded-lg px-3 py-1.5 text-xs text-white placeholder-[#4A5568] focus:border-[#0099CC] focus:outline-none transition-colors"
            data-testid="chat-input" />
          <button onClick={sendMessage} disabled={!input.trim() || sending}
            className="p-1.5 rounded-lg bg-[#0099CC] text-white hover:bg-[#0088bb] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            data-testid="chat-send-btn">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
